// Renders a list of assets using ListItem

import { AssetSectionBreadcrumbs } from 'components/assets/breadcrumbs'
import ListItem from 'components/assets/list/list-item'
import LoadingAssetList from 'components/assets/list/loading'
import { AssetTabs } from 'components/assets/tabs'
import Button from 'components/inputs/button'
import EmptyView from 'components/layout/empty-view'
import AppHeader from 'components/layout/header'
import { useCurrentUser } from 'context/current-user-context'
import { FetchError } from 'errors'
import classNames from 'helpers/class-names'
import { useHashParam } from 'hooks/use-hash'
import { useSearch } from 'hooks/use-search'
import { AssetData, AssetType } from 'lib/assets/asset'
import { ASSET_REGISTRY } from 'lib/assets/registry'
import { upperFirst } from 'lodash'
import { useRouter } from 'next/router'
import { FC, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import DateLine from './date-line'

type Props = {
  assetType: AssetType
  selectedAsset?: any
  isFixedWidth?: boolean // Whether to use fixed width for the asset list or fullscreen mode
}

const AssetList: FC<Props> = ({
  assetType,
  selectedAsset,
  children,
  isFixedWidth = true,
}) => {
  const router = useRouter()

  const asof = useHashParam('asof')

  // Construct the API URL using the optional asof argument if available.
  const apiUrl = `/api/assets?type=${assetType}${
    asof ? `&asof=${encodeURIComponent(asof)}` : ''
  }`

  const { data: assets, error, mutate, isLoading } = useSWR<AssetData[]>(apiUrl)
  const currentUser = useCurrentUser()

  const typeEntry = ASSET_REGISTRY[assetType]

  const { search, setSearch, filteredItems } = useSearch<AssetData>({
    items: assets || [],
    searchableFields: ['name'],
  })

  const showTableHeader = false

  const clearSearchTerm = () => {
    setSearch('')
  }

  // Know when we are awaiting a debounce to avoid showing the wrong buttons in the empty message - temporary workaround to avoid system-wide changes
  const [awaitingDebounce, setAwaitingDebounce] = useState(false)
  useEffect(() => {
    setAwaitingDebounce(true)
    const timeout = setTimeout(() => {
      setAwaitingDebounce(false)
    }, 550)
    return () => clearTimeout(timeout)
  }, [search])

  const createNewAsset = () => {
    router.push(`/${router.asPath.split('/')[1]}/create`)
  }

  const withArticle = (str: string) => {
    // Return with an article based on the string
    return `${/^[aeiou]/i.test(str) ? 'an' : 'a'} ${str}`
  }

  const hasSearchTerm = useMemo(() => {
    return Boolean(search)
  }, [search])

  if (error) {
    throw new FetchError()
  }

  return (
    <div className="flex h-screen w-full flex-col text-center focus:outline-none">
      {/* Header */}
      <div className="flex flex-col border-b border-gray-100 text-left">
        <AppHeader
          className="mt-4"
          breadcrumbs={<AssetSectionBreadcrumbs />}
          button={
            !currentUser.readOnly && (
              <Button
                primary
                className="h-7 px-4 py-0.5 text-sm sm:h-auto sm:px-8 sm:py-2"
                href={`/${router.asPath.split('/')[1]}/create`}
                asLink>
                Create {typeEntry.label}
              </Button>
            )
          }>
          {typeEntry.labelPlural}
        </AppHeader>
        <AssetTabs
          search={search}
          setSearch={setSearch}
          searchPlaceholder={`Search ${typeEntry.labelPlural}...`}
          searchDisabled={!Boolean(assets)}
        />
      </div>

      {/* Body */}
      <div
        className={classNames('flex flex-1 overflow-hidden', {
          'gap-3 px-2': isFixedWidth,
        })}>
        {/* Main content */}
        <div
          className={classNames('flex-1 overflow-y-auto', {
            'py-1': isFixedWidth,
          })}>
          {showTableHeader && (
            <div className="align-center flex w-full justify-start border-b border-gray-200 bg-gray-50 px-4 pb-1.5 pt-2.5 text-xs font-bold text-gray-400">
              <div>Name</div>
              <div className="flex-1" />
            </div>
          )}
          {(!assets || isLoading) && <LoadingAssetList />}
          {assets && filteredItems.length === 0 && !awaitingDebounce && (
            <EmptyView
              heading={
                assets.length !== 0
                  ? `Nothing Here`
                  : `Create Your First ${upperFirst(typeEntry.label)}`
              }
              message={
                assets.length !== 0
                  ? `No ${typeEntry.labelPlural.toLowerCase()} match your search.`
                  : `You haven't added any ${typeEntry.labelPlural.toLowerCase()} to Toolpath yet.`
              }
              buttonText={
                hasSearchTerm
                  ? `Reset Search`
                  : `Create ${withArticle(typeEntry.label)}`
              }
              buttonAction={hasSearchTerm ? clearSearchTerm : createNewAsset}
            />
          )}
          <ul
            className={classNames('flex w-full flex-col overflow-y-auto', {
              'gap-2 py-2': isFixedWidth,
            })}>
            {filteredItems.map(({ item }) => {
              const selected = item.uuid == selectedAsset?.uuid
              if (selected) {
                item = selectedAsset
              }
              return (
                <ListItem
                  asset={item}
                  selected={selected}
                  key={item.uuid}
                  onDestroy={() => {
                    mutate(assets.filter((item) => item.uuid !== item.uuid))
                  }}
                  onDuplicate={() => {
                    mutate()
                  }}
                />
              )
            })}
          </ul>
        </div>
        <div
          className={classNames('flex max-h-full', {
            'py-3': isFixedWidth,
            hidden: !children,
          })}>
          {children}
        </div>
      </div>
      <DateLine assetType={assetType} asof={asof} />
    </div>
  )
}

export default AssetList
